import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IAllLeadsResponse, ILead, INewLead, ISingleLeadRespose } from "../models/Lead";
import apiConfig from "../utils/apiConfig";

// Text for a failed collect. useApiHelper surfaces a 400's body message as the Error message,
// so this shows the API's own reason — "Cannot collect a cancelled lead" being the common one,
// which staff need to see to know the fix is to take the lead out of Cancelled first.
export function collectFailureText(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : "Failed to mark vehicle as collected";
}

// includeCompleted=false drops every Status.Completed lead from the payload.
// Completion is gated on Vehicle.CollectedOn being set, so a completed lead always
// has collectedOn != null — callers that already filter on collectedOn === null
// (the calendar views) lose nothing by excluding them server-side.
export default function useLeads(ignoreQuery = false, componentName = "useLeads", includeCompleted = true) {
  const { get, getFile, post, postGetFile, put, patch, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  if (!ignoreQuery) {
    // console.log("ignoreQuery", ignoreQuery);
  }

  const leads = useQuery({
    // "partial" separates this list from the ["leads", <leadId>] single-lead entries,
    // and includeCompleted keeps the two payload variants in their own cache slots.
    // Both still prefix-match the existing invalidateQueries(["leads"]) calls.
    queryKey: ["leads", "partial", includeCompleted],
    queryFn: () => get<IAllLeadsResponse>(`/lead/partial${includeCompleted ? "" : "?includeCompleted=false"}`),
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchInterval: 1000 * 60 * 1, // 1 minutes
    refetchIntervalInBackground: true,
    enabled: !ignoreQuery,
  });

  // const singleLead = useMutation(
  //   (leadId: string) => get(`/lead/${leadId}`),
  //   {
  //     onSuccess: (lead: ISingleLeadRespose) => {
  //       return lead;
  //     },
  //   }
  // );


  // Server-side VRM/reg lookup. Only the matching rows come back, so callers don't
  // need the whole lead list in memory to find one vehicle. No pagefilter/leadstatus
  // is sent, which leaves the results unfiltered by status — matching the behaviour
  // of searching the full list client-side.
  const searchByVrm = useMutation(
    (vrm: string) => get<IAllLeadsResponse>(`/lead/partialwithfilters?query=${encodeURIComponent(vrm)}`)
  );

  const distances = useMutation(
    (leadId: string) => get(`/lead/${leadId}/distance`),
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(["leads"]);
        return data;
      },
    }
  );

  const downloadDoc = useMutation(
    (ids: any) => getFile(`/lead/${ids.leadId}/documents/${ids.docId}`),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const getCollectionNote = useMutation(
    (leadId: string) => getFile(`/lead/${leadId}/documents/note`),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const getAllCollectionNotes = useMutation(
    (body: any) => postGetFile(`/Utilities/notes`, body),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const create = useMutation(
    (body: any) => post(`/lead?lookupLocation=true`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
      },
    }
  );

  const createMultiple = useMutation(
    (body: any) => post(`/lead/multiple`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
      },
    }
  );

  const createMultipleCompleted = useMutation(
    (body: any) => post(`/lead/multiple/completed`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const update = useMutation(
    (lead: ILead) => put(`/lead/${lead.id}`, lead),
    {
      onMutate: async (lead: ILead) => {
        await queryClient.cancelQueries({ queryKey: ["leads"] });
        const previousLead = queryClient.getQueryData(["leads", lead.id]);

        queryClient.setQueryData(["leads", lead.id], {
          success: true,
          message: "Optimistically updated",
          data: lead,
        });

        return { previousLead, lead };
      },
      onError: (err, lead, context) => {
        queryClient.setQueryData(["leads", context?.lead.id], context?.previousLead);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
        queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
      },
    }
  );

  const updateCallCount = useMutation(
    ({ leadId, delta }: { leadId: string; delta: number }) =>
      patch<ISingleLeadRespose>(`/lead/${leadId}/call-count`, { delta }),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const addCallNote = useMutation(
    ({ leadId, message, date, callCount }: { leadId: string; message: string; date?: string; callCount?: number }) =>
      post(`/lead/${leadId}/call-note`, { message, date, callCount }),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const toggleLeadCollectionDropOff = useMutation(
    (leadId: string) => put(`/lead/${leadId}/toggleCollection`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
      },
    }
  );

  // Collection state is per-lead, but one Vehicle row is shared by every lead on the
  // registration — so a vehicle-level update can't say which quote was honoured and the API has
  // to infer one. This names the lead explicitly. collectedOn is optional; omit it to stamp now.
  const markLeadCollected = useMutation(
    ({ leadId, collectedOn }: { leadId: string; collectedOn?: string | null }) =>
      put(
        `/driver/${leadId}/collected${collectedOn ? `?collectedOn=${encodeURIComponent(collectedOn)}` : ""}`,
        null
      ),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
      },
    }
  );

  const undropLead = useMutation(
    (leadId: string) => put(`/trip/${leadId}/undrop`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["unscheduledLeads"]);
        queryClient.invalidateQueries(["scheduledLeads"]);
      },
    }
  );

  const bulkUpdate = useMutation(
    (body: any) => post(`/lead/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const bulkComplete = useMutation(
    (body: any) => post(`/lead/bulk/complete`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const bulkSubmitCod = useMutation(
    (body: any) => post(`/lead/bulk/cod`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const remove = useMutation(
    (leadId: string) => del(`/lead/${leadId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    leads,
    // singleLead,
    searchByVrm,
    distances,
    downloadDoc,
    getCollectionNote,
    getAllCollectionNotes,
    create,
    createMultiple,
    createMultipleCompleted,
    update,
    updateCallCount,
    addCallNote,
    toggleLeadCollectionDropOff,
    markLeadCollected,
    undropLead,
    bulkUpdate,
    bulkComplete,
    bulkSubmitCod,
    remove
  };
}
